import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { useAppDispatch, useAppSelector } from "../app/hooks";
import {
  selectApplyStatus,
  selectMyPartnerLoadingStatus,
  selectMyPartnerStatus,
} from "../features/referrals/referrals-slice";
import {
  applyAsPartner,
  fetchMyPartnerStatus,
} from "../features/referrals/slice-extensions/referrals-slice-extension";
import { LoadingStatus } from "../features/sessions/models/LoadingStatus";
import LeftMenu from "../components/LeftMenu";
import Breadcrumbs from "../components/Breadcrumbs";
import ReferralPartnerPanel from "../components/referrals/ReferralPartnerPanel";
import { getDisplayLocale } from "../features/utils/timezoneUtils";

const formatMoney = (minorUnits: number, currency: string, locale: string): string =>
  new Intl.NumberFormat(locale, {
    style: "currency",
    currency,
    minimumFractionDigits: minorUnits % 100 === 0 ? 0 : 2,
    maximumFractionDigits: 2,
  }).format(minorUnits / 100);

/** Refer & earn — the instructor joins the referral program and shares their link. */
const ReferralProgramPage: React.FC = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const status = useAppSelector(selectMyPartnerStatus);
  const loading = useAppSelector(selectMyPartnerLoadingStatus);
  const applyStatus = useAppSelector(selectApplyStatus);

  useEffect(() => {
    dispatch(fetchMyPartnerStatus());
  }, [dispatch]);

  const apply = async () => {
    try {
      await dispatch(applyAsPartner()).unwrap();
      toast.success(t("referrals.program.applied"));
    } catch (error) {
      toast.error((error as { message?: string })?.message || t("referrals.program.applyFailed"));
    }
  };

  const bounty = status
    ? formatMoney(status.bountyAmountMinorUnits, status.bountyCurrency, getDisplayLocale())
    : "€30";
  const threshold = status
    ? formatMoney(status.payoutThresholdMinorUnits, status.bountyCurrency, getDisplayLocale())
    : "€50";
  const trialDays = 60 + (status?.trialExtensionDays ?? 30);

  const steps = [
    t("referrals.program.step1"),
    t("referrals.program.step2", { days: trialDays }),
    t("referrals.program.step3", { bounty }),
  ];

  return (
    <section>
      <div className="flex h-full gap-4">
        <LeftMenu />
        <div className="w-full p-4 pb-[120px] lg:px-10 lg:pb-10">
          <div className="lg:max-w-[1280px]">
            <Breadcrumbs />
            <div className="space-y-4 py-6 lg:space-y-6">
              <div>
                <h1>{t("referrals.program.title")}</h1>
                <p className="text-grey-40 dark:text-grey-70">
                  {t("referrals.program.subtitle", { bounty })}
                </p>
              </div>

              <div className="with-border rounded-2xl p-4 lg:p-6">
                <h2 className="text-lg font-semibold text-grey-10 dark:text-white">
                  {t("referrals.program.howTitle")}
                </h2>
                <ol className="mt-3 space-y-2">
                  {steps.map((step, index) => (
                    <li key={step} className="flex items-start gap-3">
                      <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary-95 text-xs font-semibold text-primary-40 dark:bg-primary-30 dark:text-primary-90">
                        {index + 1}
                      </span>
                      <span className="text-grey-30 dark:text-grey-70">{step}</span>
                    </li>
                  ))}
                </ol>
                <p className="mt-4 text-sm text-grey-50 dark:text-grey-60">
                  {t("referrals.program.payoutNote", { threshold })}
                </p>
              </div>

              {loading === LoadingStatus.Failed ? (
                <div className="rounded-lg bg-red-50 p-4 text-red-700 dark:bg-red-900/20 dark:text-red-300">
                  <p>{t("referrals.program.loadFailed")}</p>
                </div>
              ) : status ? (
                <ReferralPartnerPanel
                  status={status}
                  applying={applyStatus === LoadingStatus.Loading}
                  onApply={apply}
                />
              ) : (
                <p className="py-6 text-center text-grey-40 dark:text-grey-70">
                  {t("common.loading")}
                </p>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ReferralProgramPage;
